"use client";

import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { ArrowLeft, Loader, X } from "lucide-react";
import { Product, ProductDetails } from "@/sdk/types";
import { cloneNode, getOrgAndStoreId } from "@/ui-sdk/ui-script/utils";
import { ActiveHoliday } from "@/ui-sdk/ui-script/festival-collections";
import { listenForSearchParamsPolling } from "./utils";

export interface CollectionItem {
  collectionName: string;
  image: string;
  products: string[];
}

const renderCloneNode = async (item: Product) => {
  const templateNode = window.helium.datastore.memory.html.product;

  if (!templateNode) return null;

  const existingProduct = (
    await window.helium.datastore.getProduct({
      id: window?.helium?.productCardHandle ?? "",
    })
  ).details;

  const node = cloneNode(templateNode, item, existingProduct);

  node.className += " ";
  return { __html: node.outerHTML };
};

export default function FestivalCollections({
  festivalCollections,
  activeHoliday,
}: {
  festivalCollections: CollectionItem[];
  activeHoliday: ActiveHoliday;
}) {
  const [selectedCollection, setSelectedCollection] =
    useState<CollectionItem | null>(null);
  const [loading, setIsLoading] = useState(false);

  const [productAreaLoading, setProductAreaLoading] = useState(false);
  const [collectionProducts, setCollectionProducts] = useState<{
    [key: string]: Product[];
  }>({});
  useEffect(() => {
    const stopPolling = listenForSearchParamsPolling(
      (newParams, newQueryString) => {
        window.location.reload();
      },
    );
    return () => {
      stopPolling();
    };
  }, [selectedCollection]);
  useEffect(() => {
    if (!selectedCollection) return;

    const otherElement = document.querySelector(".active-facets-mobile");

    if (otherElement) {
      otherElement.remove();
    }

    const anotherElement = document.querySelector(".product-description-short");

    if (anotherElement) {
      anotherElement.remove();
    }
  }, [selectedCollection]);

  const handleCollectionChange = useCallback(
    async (collection: CollectionItem) => {
      setSelectedCollection((prev) => {
        const isSame = prev?.collectionName === collection.collectionName;
        if (isSame) {
          window.location.reload(); // Reload on deselect
          return null;
        }
        return collection;
      });

      if (!collectionProducts[collection.collectionName]) {
        setProductAreaLoading(true);
        try {
          const productsResponse = await Promise.all(
            collection.products.map(async (productId) => {
              const response = await window.helium.datastore.getProduct({
                id: productId,
              });

              const product = response.details;

              if (!product) {
                return null;
              }

              return product;
            }),
          );

          setCollectionProducts((prev) => ({
            ...prev,
            [collection.collectionName]: productsResponse.filter(Boolean),
          }));
        } catch (error) {
          console.error(
            `Error fetching products for ${collection.collectionName}:`,
            error,
          );
        } finally {
          setProductAreaLoading(false);
        }
      }
    },
    [collectionProducts],
  );

  useEffect(() => {
    const fetchRenderedProducts = async () => {
      if (!selectedCollection || loading) return;

      const productArea = document.querySelector(
        '[data-helium-name="product-list"]',
      );

      if (productArea) {
        productArea.innerHTML = "";
      }

      setProductAreaLoading(true);

      const products =
        collectionProducts[selectedCollection.collectionName] || [];

      const productHtmls = await Promise.all(
        products.map((item) => renderCloneNode(item)),
      );

      const finalHtml = productHtmls.map((html) => html?.__html).join("");
      requestAnimationFrame(() => {
        if (productArea) {
          productArea.innerHTML = finalHtml;
        }
        setProductAreaLoading(false);
      });
    };

    fetchRenderedProducts();
  }, [selectedCollection, collectionProducts, loading]);

  if (festivalCollections.length === 0) return null;

  return (
    <>
      <div className="hp-pt-2 hp-mt-4 hp-m-auto hp-grid hp-flex-col">
        <div className="hp-mb-4">
          {/* <h1 className="hp-text-3xl hp-font-semibold">
            {activeHoliday.name} Specials Collection
          </h1> */}
        </div>
        <div
          className="swimlane2 !hp-gap-8 md:!hp-gap-0 !hp-items-start"
          style={{
            paddingLeft: "0px !important",
          }}
        >
          {festivalCollections.map((collection) => (
            <div
              key={collection.collectionName}
              className={cn(
                "hp-flex hp-flex-col hp-cursor-pointer hp-w-auto md:hp-w-[120px] hp-items-center",
              )}
              onClick={() => handleCollectionChange(collection)}
            >
              {collection?.image && (
                <img
                  src={collection?.image}
                  alt={collection.collectionName}
                  className={cn(
                    "hp-h-24 hp-rounded-full hp-w-24 hp-object-cover hp-object-top",
                    {
                      "hp-border-2 hp-border-[#bf1616] hp-p-1":
                        collection.collectionName ===
                        selectedCollection?.collectionName,
                    },
                  )}
                />
              )}

              <h4
                className="hp-mt-2 hp-px-2 hp-text-xl hp-font-medium hp-text-center hp-leading-snug"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {collection.collectionName?.split(" ").length > 2 ? (
                  <>
                    <span>
                      {collection.collectionName
                        ?.split(" ")
                        .slice(0, 2)
                        .join(" ")}
                    </span>
                    <br />
                    <span>
                      {collection.collectionName?.split(" ").slice(2).join(" ")}
                    </span>
                  </>
                ) : (
                  <span>{collection.collectionName}</span>
                )}
              </h4>
            </div>
          ))}
        </div>

        {selectedCollection?.collectionName && (
          <hr className="hp-h-px hp-my-4 hp-bg-gray-200 hp-border-0" />
        )}

        <div className="hp-max-w-[600px] hp-mx-auto">
          <h4 className="hp-text-center hp-mt-3 hp-font-medium hp-text-3xl hp-mb-4 md:hp-text-3xl hp-text-black hp-font-semibold">
            {selectedCollection?.collectionName}
          </h4>
        </div>
      </div>
      {productAreaLoading && selectedCollection && (
        <div className="hp-mt-4 hp-text-center">
          <Loader className="hp-mx-auto hp-h-4 hp-w-4 hp-animate-spin" />
        </div>
      )}
    </>
  );
}
